import type { ClientToServerEvents, ServerToClientEvents } from '@shared/events';
import { ALL_COMBOS, ALL_SYMBOLS, ComboType, GameSymbol, PlayerStatus, RoomStatus } from '@shared/enums';
import {
  BASE_POINTS,
  DRAW_BONUS_POINTS,
  DRAWS_PER_TURN,
  FINISH_BONUS_POINTS,
  MAX_TIME_BONUS,
  REEL_COUNT,
  ROUND_DURATION_MS,
  ROUND_PAUSE_MS,
  SYMBOL_VALUE,
  TOTAL_ROUNDS,
} from '@shared/game-constants';
import type {
  GameResult,
  JoinResponse,
  PlayerFinalResult,
  PlayerRoundResult,
  PublicPlayerState,
  RoomState,
  RoundResult,
} from '@shared/types';
import { validateCombo } from '@shared/combo-validator';

type SocketEventName = keyof ServerToClientEvents | 'connect' | 'disconnect';
type SocketEventHandler = (...args: any[]) => void;

type LocalSession = {
  playerId: string;
  room: RoomState;
  roundTimer?: ReturnType<typeof setTimeout>;
  transitionTimer?: ReturnType<typeof setTimeout>;
};

type StartLocalSessionResult = {
  playerId: string;
  roomId: string;
  room: RoomState;
};

class SeededRng {
  private state: number;

  constructor(seed: number) {
    this.state = seed | 0;
  }

  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
}

export class LocalSinglePlayerSocket {
  public connected = true;

  private readonly listeners = new Map<SocketEventName, Set<SocketEventHandler>>();
  private session: LocalSession | null = null;
  private rng = new SeededRng(Date.now());

  on(event: string, listener: SocketEventHandler): this {
    const key = event as SocketEventName;
    const bucket = this.listeners.get(key) ?? new Set<SocketEventHandler>();
    bucket.add(listener);
    this.listeners.set(key, bucket);
    return this;
  }

  once(event: string, listener: SocketEventHandler): this {
    const onceListener: SocketEventHandler = (...args: any[]) => {
      this.off(event, onceListener);
      listener(...args);
    };
    return this.on(event, onceListener);
  }

  off(event: string, listener?: SocketEventHandler): this {
    const key = event as SocketEventName;
    if (!listener) {
      this.listeners.delete(key);
      return this;
    }
    const bucket = this.listeners.get(key);
    if (!bucket) return this;
    bucket.delete(listener);
    if (bucket.size === 0) this.listeners.delete(key);
    return this;
  }

  emit<E extends keyof ClientToServerEvents>(
    event: E,
    ...args: Parameters<ClientToServerEvents[E]>
  ): this {
    switch (event) {
      case 'CREATE_ROOM':
      case 'JOIN_ROOM':
      case 'JOIN_RANDOM_ROOM': {
        const callback = args[args.length - 1] as ((res: JoinResponse) => void) | undefined;
        callback?.({ success: false, error: 'Offline-Einzelspieler ist aktiv.' });
        break;
      }
      case 'START_GAME':
        // Singleplayer startet direkt beim Modus-Wechsel.
        break;
      case 'LOCK_REEL': {
        const [data] = args as Parameters<ClientToServerEvents['LOCK_REEL']>;
        this.lockReel(data.reelIndex);
        break;
      }
      case 'UNLOCK_REEL': {
        const [data] = args as Parameters<ClientToServerEvents['UNLOCK_REEL']>;
        this.unlockReel(data.reelIndex);
        break;
      }
      case 'DRAW':
        this.draw();
        break;
      case 'PICK_COMBO': {
        const [data] = args as Parameters<ClientToServerEvents['PICK_COMBO']>;
        this.pickCombo(data.comboType);
        break;
      }
      case 'SUBMIT_LEADERBOARD':
        this.emitServer('ERROR', { message: 'Offline-Modus: Kein Leaderboard-Upload.' });
        break;
      case 'GET_LEADERBOARD':
        this.emitServer('LEADERBOARD', { entries: [] });
        break;
      case 'GET_ONLINE_STATS':
        this.emitServer('ONLINE_STATS', { onlineTotal: 1, inRooms: 0 });
        break;
      case 'RECONNECT': {
        const [data, callback] = args as Parameters<ClientToServerEvents['RECONNECT']>;
        this.handleReconnect(data.playerId, data.roomId, callback);
        break;
      }
      default:
        break;
    }
    return this;
  }

  connect(): this {
    if (!this.connected) {
      this.connected = true;
      this.emitLifecycle('connect');
    }
    return this;
  }

  disconnect(): this {
    if (this.connected) {
      this.connected = false;
      this.emitLifecycle('disconnect');
    }
    return this;
  }

  reset(): void {
    this.clearTimers();
    this.session = null;
    this.connected = true;
    this.listeners.clear();
  }

  startLocalSession(playerName: string): StartLocalSessionResult {
    this.clearTimers();

    const normalizedName = (playerName || '').trim().slice(0, 20);
    const playerId = this.createPlayerId();
    const roomId = this.createRoomId();
    const player = this.createFreshPlayer(playerId, normalizedName, PlayerStatus.PLAYING);

    const room: RoomState = {
      roomId,
      players: [player],
      currentRound: 0,
      totalRounds: TOTAL_ROUNDS,
      roundTimeLeft: ROUND_DURATION_MS,
      roundStartTime: Date.now(),
      roundDuration: ROUND_DURATION_MS,
      status: RoomStatus.PLAYING,
      hostId: playerId,
    };

    this.session = { playerId, room };
    this.startRound(false);

    const startedRoom = this.cloneRoom(this.session.room);
    setTimeout(() => {
      if (!this.session || this.session.playerId !== playerId) return;
      this.emitServer('ROUND_START', { room: this.cloneRoom(this.session.room) });
      this.emitServer('STATE_UPDATE', { room: this.cloneRoom(this.session.room) });
    }, 140);

    return {
      playerId,
      roomId,
      room: startedRoom,
    };
  }

  private handleReconnect(
    playerId: string,
    roomId: string,
    callback: (res: JoinResponse) => void,
  ): void {
    const session = this.session;
    if (!session) {
      callback({ success: false, error: 'Keine aktive Offline-Session.' });
      return;
    }
    if (session.playerId !== playerId || session.room.roomId !== roomId) {
      callback({ success: false, error: 'Offline-Session passt nicht.' });
      return;
    }

    callback({ success: true, playerId: session.playerId, roomId: session.room.roomId });
    this.emitServer('RECONNECTED', { room: this.cloneRoom(session.room), playerId: session.playerId });
    this.emitServer('STATE_UPDATE', { room: this.cloneRoom(session.room) });
  }

  private lockReel(reelIndex: number): void {
    const player = this.getCurrentPlayer();
    if (!player || !this.canMutatePlayer(player)) return;
    if (reelIndex < 0 || reelIndex >= REEL_COUNT) return;
    if (player.lockedReels[reelIndex]) return;
    player.lockedReels[reelIndex] = true;
    this.emitStateAndMaybeEndRound();
  }

  private unlockReel(reelIndex: number): void {
    const player = this.getCurrentPlayer();
    if (!player || !this.canMutatePlayer(player)) return;
    if (reelIndex < 0 || reelIndex >= REEL_COUNT) return;
    if (!player.lockedReels[reelIndex]) return;
    player.lockedReels[reelIndex] = false;
    this.emitStateAndMaybeEndRound();
  }

  private draw(): void {
    const player = this.getCurrentPlayer();
    if (!player || player.status !== PlayerStatus.PLAYING) return;
    if (player.drawsLeft <= 0) return;

    player.reels = player.reels.map((symbol, index) => (
      player.lockedReels[index] ? symbol : this.rng.pick(ALL_SYMBOLS)
    ));
    player.drawsLeft -= 1;
    this.emitStateAndMaybeEndRound();
  }

  private pickCombo(comboType: ComboType): void {
    const session = this.session;
    const player = this.getCurrentPlayer();
    if (!session || !player || player.status !== PlayerStatus.PLAYING) return;

    const comboIndex = player.remainingCombos.indexOf(comboType);
    if (comboIndex < 0) return;
    if (!validateCombo(player.reels, comboType)) return;

    const timeLeft = this.getTimeLeft();
    const score = this.calculateComboScore(comboType, player.reels, timeLeft, player.drawsLeft);

    player.remainingCombos.splice(comboIndex, 1);
    player.comboScores[comboType] = score;
    player.roundScore += score;

    if (player.remainingCombos.length === 0) {
      player.roundScore += this.calculateFinishBonus(timeLeft);
      player.status = PlayerStatus.FINISHED;
    } else {
      player.drawsLeft = DRAWS_PER_TURN;
      player.lockedReels = Array(REEL_COUNT).fill(false);
      player.reels = this.generateReels();
    }

    this.emitStateAndMaybeEndRound();
  }

  private startRound(emitRoundStart = true): void {
    const session = this.session;
    if (!session) return;

    const room = session.room;
    const player = room.players[0];

    room.currentRound += 1;
    room.status = RoomStatus.PLAYING;
    room.roundStartTime = Date.now();
    room.roundTimeLeft = ROUND_DURATION_MS;

    player.status = PlayerStatus.PLAYING;
    player.reels = this.generateReels();
    player.lockedReels = Array(REEL_COUNT).fill(false);
    player.drawsLeft = DRAWS_PER_TURN;
    player.roundScore = 0;
    player.remainingCombos = [...ALL_COMBOS];
    player.comboScores = {};

    session.roundTimer = setTimeout(() => this.endRound(), ROUND_DURATION_MS);

    if (emitRoundStart) {
      this.emitServer('ROUND_START', { room: this.cloneRoom(room) });
      this.emitServer('STATE_UPDATE', { room: this.cloneRoom(room) });
    }
  }

  private endRound(): void {
    const session = this.session;
    if (!session) return;

    const room = session.room;
    if (room.status !== RoomStatus.PLAYING) return;

    this.clearRoundTimer();
    room.status = RoomStatus.ROUND_END;
    room.roundTimeLeft = 0;

    const player = room.players[0];
    player.totalScore += player.roundScore;
    player.roundScores.push(player.roundScore);

    const playerResults: PlayerRoundResult[] = [{
      id: player.id,
      name: player.name,
      roundScore: player.roundScore,
      totalScore: player.totalScore,
      finished: player.status === PlayerStatus.FINISHED,
    }];

    const result: RoundResult = {
      round: room.currentRound,
      playerResults,
    };

    this.emitServer('ROUND_END', { result, room: this.cloneRoom(room) });

    if (room.currentRound >= TOTAL_ROUNDS) {
      session.transitionTimer = setTimeout(() => this.endGame(), ROUND_PAUSE_MS);
      return;
    }

    session.transitionTimer = setTimeout(() => this.startRound(true), ROUND_PAUSE_MS);
  }

  private endGame(): void {
    const session = this.session;
    if (!session) return;

    const room = session.room;
    room.status = RoomStatus.GAME_END;
    room.roundTimeLeft = 0;

    const player = room.players[0];
    const rankings: PlayerFinalResult[] = [{
      id: player.id,
      name: player.name,
      totalScore: player.totalScore,
      rank: 1,
      roundScores: [...player.roundScores],
      isWinner: true,
    }];

    const result: GameResult = { rankings };
    this.emitServer('GAME_END', { result, room: this.cloneRoom(room) });
  }

  private emitStateAndMaybeEndRound(): void {
    const session = this.session;
    if (!session) return;
    this.emitServer('STATE_UPDATE', { room: this.cloneRoom(session.room) });

    const player = this.getCurrentPlayer();
    if (!player) return;
    if (player.status === PlayerStatus.FINISHED || !this.canPlayerContinue(player)) {
      this.endRound();
    }
  }

  private canMutatePlayer(player: PublicPlayerState): boolean {
    if (player.status !== PlayerStatus.PLAYING) return false;
    if (!this.canPlayerContinue(player)) return false;
    return true;
  }

  private canPlayerContinue(player: PublicPlayerState): boolean {
    if (player.status !== PlayerStatus.PLAYING) return false;
    if (player.drawsLeft > 0) return true;
    return player.remainingCombos.some((comboType) => validateCombo(player.reels, comboType));
  }

  private resolveScoringComboType(selectedComboType: ComboType, reels: GameSymbol[]): ComboType {
    let bestCombo = selectedComboType;
    let bestBase = BASE_POINTS[selectedComboType] ?? 0;

    for (const comboType of ALL_COMBOS) {
      if (!validateCombo(reels, comboType)) continue;
      const base = BASE_POINTS[comboType] ?? 0;
      if (base > bestBase) {
        bestBase = base;
        bestCombo = comboType;
      }
    }

    return bestCombo;
  }

  private calculateComboScore(
    comboType: ComboType,
    reels: GameSymbol[],
    timeLeftMs: number,
    drawsLeft: number,
  ): number {
    const scoringCombo = this.resolveScoringComboType(comboType, reels);
    const base = BASE_POINTS[scoringCombo] ?? 0;
    const symbolSum = reels.reduce((sum, symbol) => sum + (SYMBOL_VALUE[symbol] ?? 0), 0);
    const timeBonus = Math.floor((timeLeftMs / ROUND_DURATION_MS) * MAX_TIME_BONUS);
    const drawBonus = drawsLeft * DRAW_BONUS_POINTS;
    return base + symbolSum + timeBonus + drawBonus;
  }

  private calculateFinishBonus(timeLeftMs: number): number {
    const timeBonus = Math.floor((timeLeftMs / ROUND_DURATION_MS) * FINISH_BONUS_POINTS);
    return FINISH_BONUS_POINTS + timeBonus;
  }

  private generateReels(): GameSymbol[] {
    const reels: GameSymbol[] = [];
    for (let i = 0; i < REEL_COUNT; i++) reels.push(this.rng.pick(ALL_SYMBOLS));
    return reels;
  }

  private getTimeLeft(): number {
    const session = this.session;
    if (!session) return 0;
    const room = session.room;
    if (room.status !== RoomStatus.PLAYING) return room.roundTimeLeft;
    const elapsed = Date.now() - room.roundStartTime;
    return Math.max(0, room.roundDuration - elapsed);
  }

  private getCurrentPlayer(): PublicPlayerState | null {
    return this.session?.room.players[0] ?? null;
  }

  private createFreshPlayer(id: string, name: string, status: PlayerStatus): PublicPlayerState {
    return {
      id,
      name,
      reels: this.generateReels(),
      lockedReels: Array(REEL_COUNT).fill(false),
      drawsLeft: DRAWS_PER_TURN,
      roundScore: 0,
      totalScore: 0,
      roundScores: [],
      remainingCombos: [...ALL_COMBOS],
      comboScores: {},
      status,
    };
  }

  private cloneRoom(room: RoomState): RoomState {
    return {
      ...room,
      roundTimeLeft: this.getTimeLeft(),
      players: room.players.map((player) => ({
        ...player,
        reels: [...player.reels],
        lockedReels: [...player.lockedReels],
        roundScores: [...player.roundScores],
        remainingCombos: [...player.remainingCombos],
        comboScores: { ...player.comboScores },
      })),
    };
  }

  private clearTimers(): void {
    this.clearRoundTimer();
    if (this.session?.transitionTimer) {
      clearTimeout(this.session.transitionTimer);
      this.session.transitionTimer = undefined;
    }
  }

  private clearRoundTimer(): void {
    if (this.session?.roundTimer) {
      clearTimeout(this.session.roundTimer);
      this.session.roundTimer = undefined;
    }
  }

  private emitLifecycle(event: 'connect' | 'disconnect'): void {
    const listeners = this.listeners.get(event);
    if (!listeners) return;
    [...listeners].forEach((listener) => listener());
  }

  private emitServer(event: keyof ServerToClientEvents, data: unknown): void {
    const listeners = this.listeners.get(event);
    if (!listeners || listeners.size === 0) return;
    [...listeners].forEach((listener) => {
      listener(data);
    });
  }

  private createPlayerId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private createRoomId(): string {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(6);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
    }
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return result;
  }
}
